import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en.json';
import fa from '../locales/fa.json';
import es from '../locales/es.json';

// UI LANGUAGE LOCK. The UI renders in ONE language for everyone: no browser detection, no
// persisted preference, no switcher anywhere. (LanguageSwitcher.js still exists but is imported
// by NOTHING — it is the seam for a future unlock, not a live control. Verified 2026-08-24.)
//
// History, so the current value is not mistaken for an accident:
//   2026-08-04 (Ali) — locked to Persian.
//   2026-08-17 (Ali) — the accounts in EN_UI_ACCOUNTS rendered in English for the beverage demo.
//   2026-08-24 (Ali) — "turn it to english ... for now only english login and english ui be
//     visible". UI_LANGUAGE is now 'en', so EVERY account gets English and EN_UI_ACCOUNTS is
//     INERT. It is kept rather than deleted because flipping UI_LANGUAGE back to 'fa' then
//     restores the previous behaviour exactly, demo account included.
//
// TO CHANGE THE UI LANGUAGE, EDIT UI_LANGUAGE — nothing else. The multilingual machinery stays
// deliberately: all three locale files, the t() calls, the CI parity check. AI-output language is
// a SEPARATE axis with its own controls (the build-pack Prompt-language picker), so this constant
// does not change what the model writes.
export const SUPPORTED_LANGUAGES = ['en', 'fa', 'es'];
export const RTL_LANGUAGES = ['fa'];
const UI_LANGUAGE = 'en';
const EN_UI_ACCOUNTS = ['user@shetabo.com'];

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fa: { translation: fa },
      es: { translation: es },
    },
    lng: UI_LANGUAGE,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: { escapeValue: false },
  });

// Apply layout direction + lang at the document level so the WHOLE UI follows
// the active language (RTL for Farsi).
function applyDir(lang) {
  const dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lang);
}

applyDir(UI_LANGUAGE);

// The language the UI is currently locked to — decided ONLY by applyAccountLanguage below, never
// by a user control. With UI_LANGUAGE = 'en' this is 'en' for everyone.
let activeLanguage = UI_LANGUAGE;

/** Called by AuthContext whenever the session changes. Resolves to UI_LANGUAGE for everyone,
 *  except the (currently inert) EN_UI_ACCOUNTS emails which resolve to English. While
 *  UI_LANGUAGE is 'en' both branches agree, so every session renders in English. */
export function applyAccountLanguage(email) {
  const normalized = typeof email === 'string' ? email.trim().toLowerCase() : '';
  const next = EN_UI_ACCOUNTS.includes(normalized) ? 'en' : UI_LANGUAGE;
  if (next === activeLanguage && i18n.language === next) return;
  activeLanguage = next;
  i18n.changeLanguage(next);
  applyDir(next);
}

/** The lang whose admin text-overrides should be fetched — follows the active UI language. */
export function activeUiLanguage() {
  return activeLanguage;
}

// Kept as a no-op-shaped seam: the UI language is locked per account. Callers that used to
// toggle the language now get the locked language back regardless; the one legit "caller" left
// is a future decision to unlock — made here, not scattered across components.
export function setAppLanguage() {
  if (i18n.language !== activeLanguage) i18n.changeLanguage(activeLanguage);
  applyDir(activeLanguage);
}

export function isRtl(lang) {
  return RTL_LANGUAGES.includes(lang || i18n.language);
}

// ---- editable wording (migration 067) -----------------------------------------------------
//
// Every phrase resolves through a t() lookup against the bundles above. An admin can override any
// of them from inside the app; those overrides live in the database and are merged ON TOP of the
// shipped bundle here, at start-up.
//
// Why on top rather than replacing the file: the file stays the source of truth, so CI's key-parity
// check is untouched and an override can only change TEXT — never add or remove a key. Clearing an
// override restores the shipped wording exactly, because the shipped wording was never overwritten.
//
// The keys arrive FLAT and dotted ("corpusDatasets.title"). i18next stores them nested, so they are
// expanded before merging — a flat key merged as-is would sit beside the nested tree and never be
// found by t().
function expand(flat) {
  const out = {};
  for (const [dotted, value] of Object.entries(flat || {})) {
    if (typeof value !== 'string') continue;
    const parts = String(dotted).split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const p = parts[i];
      if (typeof node[p] !== 'object' || node[p] === null) node[p] = {};
      node = node[p];
    }
    node[parts[parts.length - 1]] = value;
  }
  return out;
}

/** Merge a flat {key: text} map over the live bundle. Exported so the editor can apply a save
 *  immediately, without a reload. */
export function applyTextOverrides(lang, flat) {
  const nested = expand(flat);
  if (Object.keys(nested).length === 0) return;
  // deep = true, overwrite = true: replace only the leaves present, leave everything else alone.
  i18n.addResourceBundle(lang, 'translation', nested, true, true);
  // Re-render everything currently on screen. Without this a saved edit only shows on the next
  // navigation, which reads as "the save did not work".
  i18n.emit('languageChanged', i18n.language);
}

/** Fetch and apply the stored overrides. Failure is deliberately silent: the app must render with
 *  its shipped wording rather than not render at all. */
export async function loadTextOverrides(fetcher) {
  try {
    const lang = activeUiLanguage();
    const r = await fetcher('get', { lang });
    applyTextOverrides(lang, r?.overrides || {});
  } catch {
    /* shipped wording stands */
  }
}

export default i18n;
